import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  Signal,
} from '@angular/core';
import {
  FileSaverService,
  FilesizePipe,
  Icon,
  IconComponent,
  ImageThumbnailComponent,
  ImageThumbnailUnsupportedComponent,
  SpinnerComponent,
} from '@web/shared/ui';
import {
  NgClass,
  NgOptimizedImage,
  NgTemplateOutlet,
  UpperCasePipe,
} from '@angular/common';
import { MultimediaTileMode } from '../../types/multimedia-tile-mode';
import { MultimediaTileThumbnailFilling } from '../../types/multimedia-tile-thumbnail-filling';
import { isNil, Nillable } from '@web/utils';
import { MultimediaType } from '../../enums/multimedia-type';
import { MultimediaTileBoxComponent } from '../multimedia-tile-box/multimedia-tile-box.component';
import { MultimediaTileBoxRemoveButtonComponent } from '../multimedia-tile-box-remove-button/multimedia-tile-box-remove-button.component';
import { Tooltip } from 'primeng/tooltip';

type PreviewTemplateData = {
  size: number;
  extension: string;
  filename: string;
  alt: string;
  previewUrl: string;
};

@Component({
  selector: 'app-multimedia-tile',
  templateUrl: './multimedia-tile.component.html',
  styleUrl: './multimedia-tile.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    SpinnerComponent,
    FilesizePipe,
    ImageThumbnailComponent,
    NgOptimizedImage,
    NgTemplateOutlet,
    UpperCasePipe,
    NgClass,
    ImageThumbnailUnsupportedComponent,
    MultimediaTileBoxComponent,
    MultimediaTileBoxRemoveButtonComponent,
    IconComponent,
    Tooltip,
  ],
})
export class MultimediaTileComponent {
  public readonly type = input<MultimediaType>();
  public readonly info = input<boolean>(true);
  public readonly previewUrl = input<string>();
  public readonly downloadUrl = input<string>();
  public readonly alt = input<string>();
  public readonly filename = input<string>();
  public readonly size = input<number>();
  public readonly extension = input<string>();
  public readonly filling = input<MultimediaTileThumbnailFilling>('contain');
  public readonly mode = input<MultimediaTileMode>('preview');
  public readonly isSelected = input(false);
  public readonly removeClickEvent = output();
  public readonly previewClickEvent = output();

  protected readonly previewTemplateData = this.previewTemplateDataSignal();
  protected readonly isLoadingMode = this.isLoadingModeSignal();
  protected readonly isPreviewMode = this.isPreviewModeSignal();
  protected readonly isSelectMode = this.isSelectModeSignal();
  protected readonly isRemoveMode = this.isRemoveModeSignal();
  protected readonly isDownloadMode = this.isDownloadModeSignal();
  protected readonly borderCssClass = this.borderCssClassSignal();
  protected readonly isImageType = this.isImageTypeSignal();
  protected readonly isDocumentType = this.isDocumentTypeSignal();
  protected readonly isArchiveType = this.isArchiveTypeSignal();
  protected readonly Icon = Icon;

  private readonly fileSaver = inject(FileSaverService);

  protected handlePreviewClick(): void {
    this.previewClickEvent.emit();
  }

  protected handleRemoveClickEvent(): void {
    this.removeClickEvent.emit();
  }

  protected handleDownloadClick(): void {
    const url = this.downloadUrl();

    if (isNil(url)) {
      return;
    }

    this.fileSaver.saveAsFromLink(url, this.filename()).subscribe();
  }

  protected previewFillingCssClass(): string {
    switch (this.filling()) {
      case 'contain':
        return 'object-contain';

      case 'cover':
        return 'object-cover';

      default:
        return '';
    }
  }

  private isLoadingModeSignal(): Signal<boolean> {
    return computed(() => this.mode() === 'loading');
  }

  private isPreviewModeSignal(): Signal<boolean> {
    return computed(() => this.mode() === 'preview');
  }

  private isSelectModeSignal(): Signal<boolean> {
    return computed(() => this.mode() === 'select');
  }

  private isRemoveModeSignal(): Signal<boolean> {
    return computed(() => this.mode() === 'remove');
  }

  private isDownloadModeSignal(): Signal<boolean> {
    return computed(() => this.mode() === 'download');
  }

  private borderCssClassSignal(): Signal<string> {
    return computed(() =>
      this.isSelected() ? 'border-black' : 'border-transparent',
    );
  }

  private previewTemplateDataSignal(): Signal<Nillable<PreviewTemplateData>> {
    return computed(() => {
      const size = this.size();
      const extension = this.extension();
      const filename = this.filename();
      const alt = this.alt();
      const previewUrl = this.previewUrl();

      if (
        isNil(size) ||
        isNil(extension) ||
        isNil(filename) ||
        isNil(alt) ||
        isNil(previewUrl)
      ) {
        return null;
      }

      return {
        size,
        extension,
        filename,
        alt,
        previewUrl,
      };
    });
  }

  private isImageTypeSignal(): Signal<boolean> {
    return computed(() => this.type() === MultimediaType.Image);
  }

  private isDocumentTypeSignal(): Signal<boolean> {
    return computed(() => this.type() === MultimediaType.Document);
  }

  private isArchiveTypeSignal(): Signal<boolean> {
    return computed(() => this.type() === MultimediaType.Archive);
  }
}
